import { Node, nodeInputRule } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer, mergeAttributes } from "@tiptap/react";

import { uploadImagePlugin, UploadFn } from "./uploadImagePlugin";
import { getArticleImageUrl } from "../../../../../utils/cloudinary";

import './Image.scss';

interface ImageOptions {
	inline: boolean;
	HTMLAttributes: Record<string, any>;
}

interface ImageAttributes {
	src: string;
	publicId: string
	alt?: string;
	title?: string;
}

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		image: {
			insertImage: (attrs: ImageAttributes) => ReturnType;
			updateImage: (attrs: ImageAttributes) => ReturnType;
		};
	}
}

const IMAGE_INPUT_REGEX = /!\[(.+|:?)\]\((\S+)(?:(?:\s+)["'](\S+)["'])?\)/;

const ImageNodeView = ({ node }) => {
  const { src, alt, alignment } = node.attrs;

  return (
    <NodeViewWrapper data-alignment={alignment}>
      <img src={src} alt={alt} data-alignment={alignment} />
    </NodeViewWrapper>
  );
};

export const Image = (uploadFn: UploadFn) => {
	return Node.create<ImageOptions>({
		name: "image",

    addNodeView() {
      return ReactNodeViewRenderer(ImageNodeView);
    },

		addOptions() {
			return {
				inline: false,
				HTMLAttributes: {}
			}
		},

		inline() {
			return this.options.inline;
		},

		group() {
			return this.options.inline ? "inline" : "block";
		},

		draggable: true,

		addAttributes() {
			return {
				src: {
					default: null,
				},
				alt: {
					default: null,
				},
				title: {
					default: null,
				},
				publicId: {
					default: null,
				},
        interscroller: {
          default:  false,
        },
        alignment: {
          default: 'left',
          renderHTML: (attrs) => ({
            'data-alignment': attrs.alignment,
          }),
          parseHTML: (element) => {
            return element.getAttribute('data-alignment') || 'left'
          },
        }
			};
		},

		parseHTML: () => [
			{
				tag: "img[src]",
				getAttrs: dom => {
					if (typeof dom === "string") return {};
					const element = dom as HTMLImageElement;

					const obj = {
						src: element.getAttribute("src"),
						title: element.getAttribute("title"),
						alt: element.getAttribute("alt"),
						alignment: element.getAttribute("data-alignment") || 'left',
						publicId: ''
					};
					return obj;
				},
			},
		],
		renderHTML: ({ HTMLAttributes: { publicId, interscroller, src, ...renderedAttributes} }) => {
      return ["img", mergeAttributes(renderedAttributes, {src: getArticleImageUrl(publicId), class: interscroller ? 'interscroller' : undefined })]
    },

		addCommands() {
			return {
				insertImage: attrs => ({ commands }) => {
					return commands.insertContent({ type: this.name, attrs })
				},
				updateImage: attrs => ({commands}) => {
					return commands.updateAttributes(this.name, attrs)
				},
			}
		},
		/*
		* Matches following attributes in Markdown-typed image: [, alt, src, title]
		*
		* Example:
		* ![Lorem](image.jpg) -> [, "Lorem", "image.jpg"]
		* ![](image.jpg "Ipsum") -> [, "", "image.jpg", "Ipsum"]
		* ![Lorem](image.jpg "Ipsum") -> [, "Lorem", "image.jpg", "Ipsum"]
		*/
		addInputRules() {
			return [
				nodeInputRule({ find: IMAGE_INPUT_REGEX, type: this.type, getAttributes: match => {
					const [, alt, src, title] = match;

					return {
						src,
						alt,
						title,
					};
				}}),
			];
		},
		addProseMirrorPlugins() {
			return [uploadImagePlugin(uploadFn)];
		},
	});
};
